import React from 'react';
import { Mail } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { privacyContent } from '../mock/data';
import { PageHero } from './About';
import useReveal from '../hooks/useReveal';

const Privacy = () => {
  const { t } = useLang();
  useReveal();

  const sections = t(privacyContent.sections);

  return (
    <div data-testid="privacy-page">
      <PageHero
        tag={t(ui.privacy.tag)}
        title={t(ui.privacy.title)}
        image="https://images.unsplash.com/photo-1551649081-39f820bb2d3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8 reveal">
          {/* Intro */}
          <div className="space-y-5 text-[#3a3833] leading-relaxed text-lg">
            {t(privacyContent.intro).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* Numbered sections */}
          <div className="mt-14 space-y-14">
            {sections.map((section, si) => (
              <div key={si} data-testid={`privacy-section-${si + 1}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-2xl text-champagne shrink-0">{si + 1}.</span>
                  <h2 className="font-serif text-2xl md:text-3xl leading-snug">{section.heading}</h2>
                </div>

                <div className="mt-5 pl-0 md:pl-10 space-y-5 text-[#3a3833] leading-relaxed text-lg">
                  {(section.paragraphs || []).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}

                  {section.list && (
                    <ul className="space-y-3">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-champagne shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {(section.afterList || []).map((p, i) => (
                    <p key={`al-${i}`}>{p}</p>
                  ))}

                  {(section.subsections || []).map((sub, i) => (
                    <div key={`sub-${i}`} className="mt-6 border-l-2 border-champagne pl-5 space-y-4">
                      <h3 className="font-serif text-xl">{sub.heading}</h3>
                      {sub.paragraphs.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                  ))}

                  {section.addressCard && (
                    <div className="bg-secondary p-8 flex items-start gap-4" data-testid="privacy-responsible-card">
                      <div className="w-11 h-11 shrink-0 rounded-full border border-champagne flex items-center justify-center">
                        <Mail size={18} className="text-champagne" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-serif text-lg">{section.addressCard.name}</p>
                        {section.addressCard.lines.map((l, i) => (
                          <p key={i} className="text-sm text-[#6b6862]">{l}</p>
                        ))}
                        <a
                          href={`mailto:${section.addressCard.email}`}
                          className="inline-block text-sm text-champagne hover:underline pt-1"
                          data-testid="privacy-responsible-email"
                        >
                          {section.addressCard.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {(section.afterCard || []).map((p, i) => (
                    <p key={`ac-${i}`}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-14 text-sm text-[#6b6862] italic" data-testid="privacy-last-updated">
            {t(privacyContent.lastUpdated)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
